import { api, REQUEST_TYPE } from 'store/api'
import { apiEndpointProfiler } from 'utils/rtkUtils'
import { logWarn } from 'utils/logUtils'
import { ModuleTags } from './consts'

// GREYFINCH_NOT_CONNECTED — the EXPECTED state the first time the flag is flipped for a practice that
// hasn't connected the app (the panel renders its own "not connected" copy for it, see greyfinchPanelState).
const NOT_CONNECTED_STATUS = 424

// Query builder is a named export so it can be unit-tested directly (RTK Query does not re-expose the
// raw `query` fn on `api.endpoints.*`).
export const buildSummaryQuery = ({ grinPatientId }) => ({
  type: REQUEST_TYPE.GET,
  path: `/pms/v1/greyfinch/patients/${grinPatientId}/summary`
})

// Telemetry for the summary read (exported so the 424 branch is unit-tested directly). apiEndpointProfiler
// records outcome + latency (matching the other RTK modules) BUT logs every failure at error, and 424
// GREYFINCH_NOT_CONNECTED is expected, not a fault. refetchOnMountOrArgChange refetches per patient-open,
// so letting 424 reach the error channel would page the providers-webapp monitor once per unconnected-
// practice patient. Intercept 424 at warn; delegate success and every real failure to apiEndpointProfiler.
export const greyfinchSummaryTelemetry = async (args, { queryFulfilled }) => {
  // Captured before the first await so the profiler's `took` still spans dispatch→settle (we await
  // queryFulfilled below to inspect the 424, which would otherwise collapse its internal timer to ~0).
  const startTime = Date.now()
  try {
    await queryFulfilled
  } catch (ex) {
    const err = ex?.error ?? ex
    const status = err?.response?.status ?? err?.status
    if (status === NOT_CONNECTED_STATUS) {
      logWarn('fetchGreyfinchSummary: practice not connected (424)')
      return
    }
  }
  // NOTE: deliberately no extraParams — `args` is `{ grinPatientId }`, and logUtils forwards context to
  // Datadog in staging/prod. The endpoint name + error + latency are enough to see the panel failing
  // (the observability this telemetry exists for) without exporting a patient identifier per open.
  await apiEndpointProfiler({ endpointName: 'fetchGreyfinchSummary', queryFulfilled, startTime })
}

// Reads Spec 1's per-patient Greyfinch summary (Tx note + last-5 past / next-5 upcoming appointments).
// Linking is orchestrated on Greyfinch's side (Sync patient-match), so there is no link mutation here:
// the panel simply renders `{ linked: false }` as a quiet "not linked yet" state.
export const Greyfinch = api.injectEndpoints({
  endpoints: build => ({
    fetchGreyfinchSummary: build.query({
      query: buildSummaryQuery,
      providesTags: [ModuleTags.GreyfinchSummary],
      // The shared base query swallows errors (store/api.js catch → { error }) and the backend logs these
      // at warn, so without telemetry a failing panel is invisible on both ends. apiEndpointProfiler records
      // the outcome + latency (matching the other RTK modules, e.g. NotificationCenter) — BUT it logs every
      // failure at error, and 424 GREYFINCH_NOT_CONNECTED is expected, not a fault. refetchOnMountOrArgChange
      // refetches per patient-open, so letting 424 reach the error channel would page the providers-webapp
      // monitor once per unconnected-practice patient. Intercept 424 at warn; delegate everything else.
      onQueryStarted: greyfinchSummaryTelemetry
    })
  })
})

export const { useFetchGreyfinchSummaryQuery } = Greyfinch
